import { useState } from 'react';
import { addToast } from '@heroui/react';
import {
  StudentRequestReason,
  useUpdateStudentRequestMutation,
  type IStudentRequestDetail,
} from '@/entities/StudentRequests';
import { AppButton } from '@/shared/ui/AppButton';
import { DateSelectField, SelectField, TextareaField } from '@/shared/ui/Fields';
import { RequestAttachmentList } from './RequestAttachmentList';
import { MAX_REQUEST_FILES, RequestFilesField } from './RequestFilesField';
import { STUDENT_ATTENDANCE_REASON_OPTIONS, STUDENT_GENERAL_REASON_OPTIONS } from './requestUi';

interface RequestEditFormProps {
  request: IStudentRequestDetail;
  onDone: () => void;
}

const getErrorMessage = (error: unknown) => {
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message);
  }

  return 'Failed to save the request';
};

// Дата приходит с сервера полным ISO, а поле даты работает с YYYY-MM-DD.
const toDateInput = (value: string | null) => value?.slice(0, 10) ?? '';

export const RequestEditForm = ({ request, onDone }: RequestEditFormProps) => {
  const isAttendance = request.requestCategory === 'attendance';

  const [reason, setReason] = useState<StudentRequestReason>(request.reason);
  const [comment, setComment] = useState(request.studentComment);
  const [startDate, setStartDate] = useState(toDateInput(request.startDate));
  const [endDate, setEndDate] = useState(toDateInput(request.endDate));
  const [files, setFiles] = useState<File[]>([]);
  const [removedKeys, setRemovedKeys] = useState<string[]>([]);

  const [updateStudentRequest, { isLoading }] = useUpdateStudentRequestMutation();

  const keptAttachments = request.studentAttachments.filter(
    (attachment) => !removedKeys.includes(attachment.key),
  );
  // Занятия остаются те же: правится изложение, а не состав пропусков. Сервер требует
  // непустой список для посещаемости, поэтому отправляем его как есть.
  const groupLessonIds =
    request.attendance?.modules.flatMap((module) =>
      module.lessons.map((lesson) => lesson.groupLessonId),
    ) ?? [];

  const canSave = Boolean(reason && comment.trim()) && (!isAttendance || groupLessonIds.length > 0);

  const handleSave = async () => {
    if (!canSave) return;

    try {
      await updateStudentRequest({
        requestId: request.id,
        requestCategory: request.requestCategory,
        reason,
        studentComment: comment.trim(),
        ...(isAttendance
          ? { groupLessonIds }
          : {
              startDate: startDate ? new Date(startDate).toISOString() : null,
              endDate: endDate ? new Date(endDate).toISOString() : null,
            }),
        studentAttachments: files.length ? files : undefined,
        removeAttachmentKeys: removedKeys.length ? removedKeys : undefined,
      }).unwrap();

      addToast({ title: 'Request updated', color: 'success' });
      onDone();
    } catch (error) {
      addToast({ title: getErrorMessage(error), color: 'danger' });
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <SelectField
        name="editReason"
        label="Reason"
        placeholder="Select reason"
        value={reason}
        options={isAttendance ? STUDENT_ATTENDANCE_REASON_OPTIONS : STUDENT_GENERAL_REASON_OPTIONS}
        onChange={(value) => setReason(value as StudentRequestReason)}
      />

      {!isAttendance && (
        <div className="grid grid-cols-2 gap-5">
          <DateSelectField
            name="editStartDate"
            label="Period start (optional)"
            value={startDate}
            dateFormat="dd/MM/yyyy"
            onChange={(_, value) => setStartDate(value)}
          />
          <DateSelectField
            name="editEndDate"
            label="Period end (optional)"
            value={endDate}
            minDate={startDate ? new Date(startDate) : undefined}
            dateFormat="dd/MM/yyyy"
            onChange={(_, value) => setEndDate(value)}
          />
        </div>
      )}

      <TextareaField
        name="editComment"
        label="Comment"
        placeholder="Enter your comment"
        rows={5}
        value={comment}
        onChange={(event) => setComment(event.target.value)}
      />

      <RequestAttachmentList
        attachments={keptAttachments}
        onRemove={(key) => setRemovedKeys((current) => [...current, key])}
      />

      <RequestFilesField
        files={files}
        onChange={setFiles}
        maxFiles={Math.max(MAX_REQUEST_FILES - keptAttachments.length, 0)}
      />

      <div className="mt-4 grid grid-cols-2 gap-6">
        <AppButton color="white" height={56} onPress={onDone}>
          Cancel
        </AppButton>
        <AppButton
          color="red"
          height={56}
          disabled={!canSave}
          isLoading={isLoading}
          onPress={handleSave}
        >
          Save
        </AppButton>
      </div>
    </div>
  );
};
